import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchStreams, invalidateStreamsSearch, searchStreams } from "actions/streams";
import { useSpring, animated, useTransition } from "react-spring";
import { TabBar } from "components/tabbar";
import { AssetPlaceholder, CollectionPlaceholder, CollectionsBrowser, EntityBrowser, StreamPlaceholder, StreamsBrowser } from "components/entityBrowser";
import { SearchBar } from "components/input";
import { invalidateAssetsSearch, searchAssets } from "actions/assets";
import InfiniteScroll from "react-infinite-scroller";
import { invalidateCollectionsSearch, searchCollections } from "actions/collections";
import GenerateLink from "components/generateLink";
import { useHistory } from 'react-router-dom';
import { invalidateAssetSelection, invalidateCollectionSelection, selectAsset, selectCollection } from "actions/selection";
import { Tooltip } from "react-bootstrap";

import styles from "./contentLibrary.module.scss";
import cx from "classnames";
import _ from "lodash";
import { EmptyMessage } from "components/emptyMessage";
import AuthService from "services/auth.service";
import { PF_GET_CURRENT_TAB_DETAILS } from "constants/actionTypes";

const isInternal = (entity) => {
  switch (entity.entityType) {
    case "collection":
      return entity.internal;
    case "asset":
      return !entity.settings.share;
    default:
      return false;
  }
};

export default function ContentLibrary(props) {
  const dispatch = useDispatch();
  const history = useHistory();

  const streams = useSelector((state) => _.get(state, "streams.items")) || [];
  const activeTab = useSelector((state) => _.get(state, "application.activeTab")) || null;
  const localActiveTab = AuthService.getActiveTab() || null;
  const selectedAssets = useSelector((state) => _.get(state, "selection.assets")) || [];

  const isGmailIntegrationActivated = AuthService.isGmailIntegrationActivated() === "true" ? true : false;

  const [query, setQuery] = useState(null);
  const [generateLink, setGenerateLink] = useState(true);
  const [entity, setEntity] = useState(null);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [browseActiveTab, setBrowseActiveTab] = useState(0);
  const [searchActiveTab, setSearchActiveTab] = useState(0);
  const [attachAssets, setAttachAssets] = useState(false);

  const triggerMessageToPlugin = (message) => {
    window.parent.postMessage(message, "*");
  }

  useEffect(() => {

    if(localActiveTab && activeTab && (localActiveTab === activeTab)){
      
      const isGmailActive = isGmailIntegrationActivated && activeTab && activeTab.includes("mail.google.com");
      if (isGmailActive){
        setAttachAssets(true);
      } else {
        setAttachAssets(false);
      }
    } else {
      triggerMessageToPlugin({
        type: PF_GET_CURRENT_TAB_DETAILS,
        payload: {}
      })
    }

    if(!isGmailIntegrationActivated){
      setAttachAssets(false);
    }
  }, [activeTab, localActiveTab, isGmailIntegrationActivated])

  const handleQueryChange = (newQuery) => {
    setQuery(newQuery);
  }

  useEffect(() => {
    if (_.size(streams) === 0) {
      dispatch(fetchStreams());
    }
  }, [dispatch, streams]);

   useEffect(() => {
    if(!generateLink) {
      setEntity(null);
      if(query){
        setSearchActiveTab(0);
      } else {
        setBrowseActiveTab(0);
      }
    }
  }, [generateLink])

  useEffect(() => {
    // Reset search tab when query changes
    if(query) {
      setSearchActiveTab(0);
    }
  }, [query])

  const handleSelection = (selectedEntity) => {
    if(attachAssets){
      if(selectedEntity.entityType === "asset"){
        if(selectedEntities.some(entity => entity.id === selectedEntity.id)){
          const updatedSelectedEntities = _.filter(selectedEntities, (entity) => entity.id !== selectedEntity.id);
          setSelectedEntities(updatedSelectedEntities);
          dispatch(invalidateAssetSelection());
          updatedSelectedEntities.map(asset => dispatch(selectAsset(asset)));
          return;
        } else {
          const allAssets = [...selectedAssets, ...selectedEntities, selectedEntity];
          const uniqueAssets = _.uniqBy(allAssets, 'id');
          dispatch(invalidateAssetSelection());
          uniqueAssets.map(asset => dispatch(selectAsset(asset)));
          setSelectedEntities(uniqueAssets);
        }
      } else if(selectedEntity.entityType === "collection"){
        dispatch(invalidateCollectionSelection());
        dispatch(selectCollection(selectedEntity));
        history.push("/attachAssets");
      }
    } else {
      if(selectedEntity.entityType === "collection"){
        setEntity(selectedEntity);
        setGenerateLink(true);
      }
    }
  }

  const generateLinkForAsset = (asset) => {
    setEntity(asset);
    setGenerateLink(true);
  }

  const handleAttachItems = () => {
    history.push("/attachAssets");
  }

  const handleBackClicked = () => {
    history.push("/paperflite-home");
  }


  return (
    <div className={cx(styles.contentLibrary, {
      [styles.attachMode]: attachAssets && _.size(selectedAssets) > 0
    })}>

    {entity && <GenerateLink entity={entity} type={entity?.entityType} show={generateLink} setShow={setGenerateLink} />}

    {!entity &&(
      <>
      <p className={styles.headerContainer}>
        <span className={styles.backButton} onClick={() => handleBackClicked()}>
          <i className="paperflite-left-arrow" />
          <span className={styles.back}>streams & collections</span>
        </span>
        <span className={styles.subText}>
          Add assets from your streams & collections
        </span>
      </p>

      <div className={styles.searchBar}>
        <SearchBar query={query} onSearch={handleQueryChange} />
      </div>

      <div>
        {query ? (
          <SearchResults 
          width={100}
          query={query}
          onSelection={(selectedEntity) => handleSelection(selectedEntity)}
          allowInternalEntities={false}
          selectableEntityTypes={[]}
          activeTab = {searchActiveTab}
          setActiveTab = {setSearchActiveTab}
          attachAssets={attachAssets}
          generateLinkForAsset={generateLinkForAsset}
        />
        ) : 
          (<AssetBrowser
            width={100}
            source={""}
            className={styles.assetBrowser}
            allowInternalEntities={false}
            selectableEntityTypes={[]}
            onSelection={(selectedEntity) => handleSelection(selectedEntity)}
            entityAction={props.entityAction}
            activeTab = {browseActiveTab}
            setActiveTab = {setBrowseActiveTab}
            attachAssets={attachAssets}
            generateLinkForAsset={generateLinkForAsset}
          />)}
      </div>
      </>
    )}

    {_.size(selectedAssets) > 0 && (
      <div className={styles.footer}>
        <div className={styles.attachAssetsContainer} onClick={handleAttachItems}>
          <i className={cx(styles.sendIcon, "paperflite-cursor")} />
          <span className={styles.attachAssets}>Attach assets ({_.size(selectedAssets)})
            <i className="paperflite-close" onClick={(e) => {
              e.stopPropagation();
              setSelectedEntities([]);
              dispatch(invalidateAssetSelection())}} />
          </span>
        </div>
      </div>)}
    </div>
  );
}

const TabSection = ({ index, selected, children }) => {
  const active = index === selected;

  const animation = useSpring({
    // height: active ? 'auto' : 0,
    opacity: active ? 1 : 0,
    transform: active
      ? "translate3d(0,0,0)"
      : `translate3d(${100 * (index > selected ? 1 : -1)}%, 0, 0)`,
  });

  return (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {children}
    </animated.div>
  );
};

const AssetBrowser = ({
  style,
  width,
  source,
  className,
  allowInternalEntities,
  selectableEntityTypes,
  onSelection,
  entityAction,
  attachAssets,
  activeTab,
  setActiveTab,
  generateLinkForAsset
}) => {

  const handleSelection = (entity) => {
    if (source && (source.type === "stream" || source.type === "group")) {
      onSelection(entity);
    } else if (entity.entityType === "collection") {
      if (allowInternalEntities || !entity.internal) {
        onSelection(entity);
      }
    } else if (entity.entityType === "asset") {
      if (allowInternalEntities || entity.settings?.share) {
        onSelection(entity);
      }
    }
  };

  return (
    <animated.div
      style={style}
      className={cx(styles.assetBrowser, styles.stack)}
    >
      <TabBar
        className={cx(styles.tabs, className)}
        gap={32}
        tabSize="equal"
        active={activeTab}
        onSelectionChange={(tab) => {
          setActiveTab(tab)
          }}
      >
        <div className={cx(styles.tab, { [styles.active]: activeTab == 0 })}>
          Streams
        </div>
        <div className={cx(styles.tab, { [styles.active]: activeTab == 1 })}>
          Collections
        </div>
      </TabBar>
      <div
        className={cx(styles.stacks, styles.tabContainer)}
        style={{ width: `${width}%` }}
      >
        <TabSection index={0} selected={activeTab}>
          <StreamsBrowser
            width={width}
            action={(entity) => {
              if (
                _.size(selectableEntityTypes) > 0 &&
                _.includes(selectableEntityTypes, entity.entityType)
              ) {
                const internal = !allowInternalEntities && isInternal(entity);
                return (
                  <Tooltip
                    content="Internal use only"
                    placement="auto"
                    disabled={!internal}
                  >
                    <button className={styles.action}>
                      <i
                        className={cx(
                          styles.icon,
                          internal ? "paperflite-lock" : "paperflite-add"
                        )}
                      ></i>
                    </button>
                  </Tooltip>
                );
              }

              if (entityAction) {
                return entityAction(entity);
              }

              return null;
            }}
            onSelection={handleSelection}
            attachAssets={attachAssets}
            generateLinkForAsset={generateLinkForAsset}
          />
        </TabSection>
        <TabSection index={1} selected={activeTab}>
          <CollectionsBrowser
            width={width}
            action={(entity) => {
              if (
                _.size(selectableEntityTypes) > 0 &&
                _.includes(selectableEntityTypes, entity.entityType)
              ) {
                const internal = !allowInternalEntities && isInternal(entity);
                return (
                  <Tooltip
                    content="Internal use only"
                    placement="auto"
                    disabled={!internal}
                  >
                    <button className={styles.action}>
                      <i
                        className={cx(
                          styles.icon,
                          internal ? "paperflite-lock" : "paperflite-add"
                        )}
                      ></i>
                    </button>
                  </Tooltip>
                );
              }

              if (entityAction) {
                return entityAction(entity);
              }

              return null;
            }}
            onSelection={handleSelection}
            attachAssets={attachAssets}
            generateLinkForAsset={generateLinkForAsset}
          />
        </TabSection>
      </div>
    </animated.div>
  );
};

const SearchResults = ({width, query, allowInternalEntities, selectableEntityTypes, entityAction, onSelection, attachAssets, activeTab, setActiveTab, generateLinkForAsset}) => {

    const { streams, collections, assets } = useSelector((state) => {
      const streamState = _.get(state, "streams.search");
      const collectionState = _.get(state, "collections.search");
      const assetState = _.get(state, "assets.search");

      const streams = _.get(streamState, "pagination.totalRecordsCount", 0);
      const collections = _.get(
        collectionState,
        "pagination.totalRecordsCount",
        0,
      );
      const assets = _.get(assetState, "pagination.totalRecordsCount", 0);

      return {
        streams: streams,
        collections: collections,
        assets: assets,
      };
    }, _.isEqual);

  const handleSelection = (entity) => {
    onSelection(entity);
  }

  return (
     <animated.div
      className={cx(styles.searchResults, styles.stack)}
    >
      <TabBar
        className={cx(styles.tabs)}
        gap={32}
        tabSize="equal"
        active={activeTab}
        onSelectionChange={(tab) => {
          setActiveTab(tab)
          }}
      >
        <div className={cx(styles.tab, { [styles.active]: activeTab == 0 })}>
          Assets
          {assets > 0 ? `(${assets})` : ""}
        </div>
        <div className={cx(styles.tab, { [styles.active]: activeTab == 1 })}>
          Streams
          {streams > 0 ? `(${streams})` : ""}
        </div>
       
        <div className={cx(styles.tab, { [styles.active]: activeTab == 2 })}>
          Collections
          {collections > 0 ? `(${collections})` : ""}
        </div>
        
      </TabBar>
      <div className={cx(styles.stacks, styles.tabContainer)}>
        <TabSection index={0} selected={activeTab}>
          <AssetSearchResult
            query={query}
            width={width}
            action={(entity) => {
              if (
                _.size(selectableEntityTypes) > 0 &&
                _.includes(selectableEntityTypes, entity.entityType)
              ) {
                const internal = !allowInternalEntities && isInternal(entity);

                return (
                  <Tooltip
                    content="Internal use only"
                    placement="auto"
                    disabled={!internal}
                  >
                    <button className={styles.action}>
                      <i
                        className={cx(
                          styles.icon,
                          !entity.settings.share
                            ? "paperflite-lock"
                            : "paperflite-add",
                        )}
                      ></i>
                    </button>
                  </Tooltip>
                );
              }

              if (entityAction) {
                return entityAction(entity);
              }

              return false;
            }}
            onSelection={handleSelection}
            attachAssets={attachAssets}
            generateLinkForAsset={generateLinkForAsset}
          />
        </TabSection>
        <TabSection index={1} selected={activeTab}>
          <StreamSearchResult
            query={query}
            width={width}
            action={(entity) => {
              if (
                _.size(selectableEntityTypes) > 0 &&
                _.includes(selectableEntityTypes, entity.entityType)
              ) {
                const internal = !allowInternalEntities && isInternal(entity);

                return (
                  <Tooltip
                    content="Internal use only"
                    placement="auto"
                    disabled={!internal}
                  >
                    <button className={styles.action}>
                      <i
                        className={cx(
                          styles.icon,
                          internal ? "paperflite-lock" : "paperflite-add",
                        )}
                      ></i>
                    </button>
                  </Tooltip>
                );
              }

              if (entityAction) {
                return entityAction(entity);
              }

              return false;
            }}
            onSelection={handleSelection}
            attachAssets={attachAssets}
            generateLinkForAsset={generateLinkForAsset}
          />
        </TabSection>

          <TabSection index={2} selected={activeTab}>
            <CollectionSearchResult
              query={query}
              width={width}
              action={(entity) => {
                if (
                  _.size(selectableEntityTypes) > 0 &&
                  _.includes(selectableEntityTypes, entity.entityType)
                ) {
                  if (
                    entity.entityType === "collection" &&
                    entity.sectionsCount == 0 &&
                    entity.assetsCount == 0
                  ) {
                    return false;
                  }

                  const internal = !allowInternalEntities && isInternal(entity);

                  return (
                    <Tooltip
                      content="Internal use only"
                      placement="auto"
                      disabled={!internal}
                    >
                      <button className={styles.action}>
                        <i
                          className={cx(
                            styles.icon,
                            internal ? "paperflite-lock" : "paperflite-add",
                          )}
                        ></i>
                      </button>
                    </Tooltip>
                  );
                }

                if (entityAction) {
                  return entityAction(entity);
                }

                return false;
              }}
              onSelection={handleSelection}
              attachAssets={attachAssets}
              generateLinkForAsset={generateLinkForAsset}
            />
          </TabSection>

      </div>
    </animated.div>
  );
}

const AssetSearchResult = ({
  className,
  query,
  width,
  action,
  onSelection,
  attachAssets,
  generateLinkForAsset
}) => {
  const dispatch = useDispatch();

  const { loading, assets, nextPage, hasMoreItems } = useSelector(
    (state) => {
      const assetState = _.get(state, "assets.search");
      const loading = _.get(assetState, "searching");
      const pagination = _.get(assetState, "pagination");
      const total = _.get(pagination, "totalPages", 0);

      const assets = _.get(assetState, "items") || [];

      let nextPage= _.get(pagination, "current", 0) + 1;

      const hasMoreItems = nextPage < total;

      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        assets: assets,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    },
    _.isEqual,
  );

  useEffect(() => {
    searchAssetForPage(0);
  }, [query]);

  const searchAssetForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };
      if (page <= 0) {
        dispatch(invalidateAssetsSearch());
      }
      dispatch(searchAssets(query, 0, 20));
    }
  };

  const handleLoadMoreAssets = () => {
    if (nextPage) {
      searchAssetForPage(nextPage);
    }
  };

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll
        className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreAssets}
        loader={
          <AssetPlaceholder width={"50%"} />
        }
      >
        {_.size(assets) === 0 && !loading && (
          <NoResults />
        )}
        {_.size(assets) > 0 && (
          <EntityBrowser
            className={className}
            width={width}
            items={assets}
            action={action}
            onSelection={onSelection}
            attachAssets={attachAssets}
            generateLinkForAsset={generateLinkForAsset}
          />
        )}
      </InfiniteScroll>
    </div>
  );
};

const StreamSearchResult = ({
  className,
  query,
  width,
  action,
  onSelection,
  attachAssets,
  generateLinkForAsset
}) => {
  const dispatch = useDispatch();

  const { loading, streams, nextPage, hasMoreItems } = useSelector(
    (state) => {
      const streamState = _.get(state, "streams.search");
      const loading = _.get(streamState, "searching");
      const pagination = _.get(streamState, "pagination");
      const total = _.get(pagination, "totalPages", 0);

      const streams = _.get(streamState, "items") || [];

      let nextPage = _.get(pagination, "current", 0) + 1;

      const hasMoreItems = nextPage < total;

      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        streams: streams,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    },
    _.isEqual,
  );

  useEffect(() => {
    searchStreamForPage(0);
  }, [query]);

  const searchStreamForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };
      if (page <= 0) {
        dispatch(invalidateStreamsSearch());
      }
      dispatch(searchStreams(query, true, 0, 12, false));
    }
  };

  const handleLoadMoreStreams = () => {
    if (nextPage) {
      searchStreamForPage(nextPage);
    }
  };

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll
        className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreStreams}
        loader={
          <StreamPlaceholder width={"50%"} />
        }
        >
        {_.size(streams) === 0 && !loading && (
          <NoResults />
        )}
        {_.size(streams) > 0 && (
          <EntityBrowser
            className={className}
            width={width}
            items={streams}
            action={action}
            onSelection={onSelection}
            attachAssets={attachAssets}
            generateLinkForAsset={generateLinkForAsset}
          />
        )}
      </InfiniteScroll>
    </div>
  );
};

const CollectionSearchResult = ({
  className,
  query,
  width,
  action,
  onSelection,
  attachAssets,
  generateLinkForAsset
}) => {
  const dispatch = useDispatch();

  const { loading, collections, nextPage, hasMoreItems } = useSelector(
    (state) => {
      const collectionState = _.get(state, "collections.search");
      const loading = _.get(collectionState, "searching");
      const pagination = _.get(collectionState, "pagination");
      const total = _.get(pagination, "totalPages", 0);

      const collections = _.get(collectionState, "items");

      let nextPage = _.get(pagination, "current", 0) + 1;
      const hasMoreItems = nextPage < total;

      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        collections: collections,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    },
    _.isEqual,
  );

  useEffect(() => {
    searchCollectionForPage(0);
  }, [query]);

  const searchCollectionForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };
      if (page <= 0) {
        dispatch(invalidateCollectionsSearch());
      }
      dispatch(searchCollections(query, true, 0, 12, false));
    }
  };

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      searchCollectionForPage(nextPage);
    }
  };

  const filteredCollections = collections.filter((collection) => collection.id !== "uncategorized" && !collection.internal && !collection.dealRoom);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll
        className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreCollections}
        loader={
         <CollectionPlaceholder width={"50%"} />
        }>
        {_.size(filteredCollections) === 0 && !loading && (
          <NoResults />
        )}
        {_.size(filteredCollections) > 0 && (
          <EntityBrowser
            className={className}
            width={width}
            items={filteredCollections}
            action={action}
            onSelection={onSelection}
            attachAssets={attachAssets}
            generateLinkForAsset={generateLinkForAsset}
          />
        )}
      </InfiniteScroll>
    </div>
  );
};

const NoResults = () => {
  return (
    <EmptyMessage
      src="/images/no_results.png"
      message="No results found"
      className={styles.noResults}
    />
  )
}